import * as React from "react";
import {
  Card,
  DatePicker,
  DatePickerHandle,
  Dropdown,
  GooglePlacesSearch,
  PickerContainer,
  Table,
  Tabs,
  Text,
} from "../../components";
import type { DashboardViewModel } from "./viewModel";
import { ExpandedRowDetails } from "./ExpandedRowDetails";
import { TeleHealthBanner } from "./TeleHealthBanner";
import { ProductsParamsSortBy } from "../../apis/products";
import { MemberOptionsSkeleton } from "./MemberOptionsSkeleton";
import { startOfDay } from "date-fns";
import { SystemServiceCode } from "../../stores";

export function View({ t, ...props }: DashboardViewModel) {
  const dateRangePopoverRef = React.useRef<DatePickerHandle | null>(null);

  return (
    <div>
      <div className="bg-secondary px-[20px] lg:px-[120px] py-[20px] shadow-soft">
        <div className="grid gap-[20px] md:grid-cols-4 md:items-end">
          <GooglePlacesSearch
            label={t("dashboard.filters.chooseCity")} //searcha loc or aread
            value={props?.placeName ?? ""}
            onSelect={props.onSelectPlace}
          />
          <Dropdown
            placeholder="Select service"
            label={t("dashboard.filters.chooseService")}
            value={props.serviceTypeId}
            options={props.serviceOptions}
            onChange={(v) => props.setFilter("serviceTypeId", v)}
          />
          <div className="relative">
            <PickerContainer
              onClick={() => dateRangePopoverRef.current?.open()}
              label={t("dashboard.filters.dateRange")}
              value={`${props.dateRange.from} – ${props.dateRange.to}`}
            />
            <DatePicker
              ref={dateRangePopoverRef}
              minDate={startOfDay(new Date()).toISOString()}
            >
              <DatePicker.Range
                value={props.dateRange}
                onChange={(v) => props.setFilter("dateRange", v)}
              />
            </DatePicker>
          </div>
          <Dropdown
            placeholder="Select a language"
            label={t("dashboard.filters.serviceLanguage")}
            value={props.language}
            options={props.serviceLanguageOptions}
            onChange={(v) => props.setFilter("language", v)}
          />
        </div>
      </div>
      <Card className="mt-[16px] mb-[30px] mx-[16px] min-h-[612px]">
        <TeleHealthBanner
          coords={props.coords}
          countryCode={props.countryCode}
          onBook={(p) => props.navigateToBooking(p)}
          emptyText={({ hasTelehealthData }) =>
            props.loading || props.dataSource.length
              ? ""
              : props.coords
                ? hasTelehealthData
                  ? t("dashboard.empty.noInPersonServicesWithTelehealth")
                  : t("dashboard.empty.noInPersonServices")
                : t("dashboard.empty.selectLocation")
          }
        />
        {props.loading && !props.dataSource.length ? (
          <MemberOptionsSkeleton />
        ) : (
          !!props.dataSource.length && (
            <>
              <div className="flex items-center justify-between">
                <Text level={2}>{"Member Option"}</Text>
                <div className="flex items-center gap-[16px]">
                  <Text>{"Ranked by: "}</Text>
                  <Tabs
                    value={props.sortBy}
                    onChange={(v) => props.setFilter("sortBy", v)}
                    items={[
                      {
                        label: t("general.availability"),
                        value: ProductsParamsSortBy.availability,
                      },
                      {
                        label: t("general.price"),
                        value: ProductsParamsSortBy.price,
                      },
                      ...([SystemServiceCode.telehealth].includes(
                        props.service?.code as SystemServiceCode,
                      )
                        ? []
                        : [
                            {
                              label: t("general.distance"),
                              value: ProductsParamsSortBy.distance,
                            },
                          ]),
                    ]}
                  />
                </div>
              </div>
              <Table
                className="mt-[16px]"
                loading={props.loading}
                expandable={{
                  accordion: true,
                  expandedRowRender: (data) => <ExpandedRowDetails {...data} />,
                }}
                columns={props.columns}
                dataSource={props.dataSource}
                rowKey={(row) =>
                  `${row.id}-${row.location ?? row.partner_location?.id ?? ""}`
                }
                pagination={{
                  page: props.page,
                  pageSize: props.pageSize,
                  totalItems: props.totalItems,
                  onLoadMore: props.onLoadMore,
                }}
              />
            </>
          )
        )}
      </Card>
    </div>
  );
}
